import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import CinemaBrand from "@/components/CinemaBrand";
import {
  LayoutDashboard, Film, CalendarDays, Ticket, ClipboardCheck, Coins, Receipt,
  Popcorn, ShoppingBag, Package, Users, Gift, Monitor, BarChart3, Settings,
  UserCog, LogOut, ScanLine, Image as ImageIcon, Building2, Megaphone, Mail, Percent, Sparkles,
  TrendingUp, Printer, PenTool, Layers, ScrollText, FileText, Trophy, ShieldCheck, Lightbulb, Calculator, Palette, CreditCard, AlertTriangle, MessageSquare, Tv
} from "lucide-react";

const NAV = [
  { to: "/admin", icon: LayoutDashboard, label: "Übersicht", exact: true, roles: ["admin","staff","snack"] },
  { to: "/admin/scanner", icon: ScanLine, label: "Einlass (Handy)", roles: ["door"] },
  { to: "/admin/saal-kontrolle", icon: ShieldCheck, label: "Saal-Kontrolle (Handy)", roles: ["door"] },
  { to: "/admin/showtimes", icon: CalendarDays, label: "Vorstellungen", roles: ["admin","staff"] },
  { to: "/admin/halls", icon: Building2, label: "Säle", roles: ["admin"] },
  { to: "/admin/movies", icon: Film, label: "Filme", roles: ["admin"] },
  { to: "/admin/tickets", icon: Ticket, label: "Tickets", roles: ["admin","staff"] },
  { to: "/admin/reservations", icon: ClipboardCheck, label: "Reservierungen", roles: ["admin","staff"] },
  { to: "/admin/pos", icon: Coins, label: "Kinokasse", roles: ["admin","staff"] },
  { to: "/admin/orders", icon: Receipt, label: "Kassenbuch", roles: ["admin","staff"] },
  { to: "/admin/kassennachschau", icon: ScrollText, label: "Kassennachschau", roles: ["admin"] },
  { to: "/admin/kassensturz", icon: Calculator, label: "Z-Bericht · Kassensturz", roles: ["admin","staff"] },
  { to: "/admin/waitlist", icon: Users, label: "Warteliste", roles: ["admin","staff"] },
  { to: "/admin/flyers", icon: Palette, label: "Flyer & Prospekte", roles: ["admin","staff"] },
  { to: "/admin/betrieb", icon: Percent, label: "Betrieb", roles: ["admin"] },
  { to: "/admin/snackbar", icon: Popcorn, label: "Snackbar", roles: ["admin","staff","snack"] },
  { to: "/admin/products", icon: ShoppingBag, label: "Produkte", roles: ["admin"] },
  { to: "/admin/inventory", icon: Package, label: "Lagerbestand", roles: ["admin","staff"] },
  { to: "/admin/guests", icon: Users, label: "Gäste & Mitglieder", roles: ["admin","staff"] },
  { to: "/admin/abos", icon: CreditCard, label: "Abos & Monatskarten", roles: ["admin","staff"] },
  { to: "/admin/druck-werkstatt", icon: AlertTriangle, label: "Druck-Werkstatt", roles: ["admin","staff"] },
  { to: "/admin/vouchers", icon: Gift, label: "Gutscheine", roles: ["admin"] },
  { to: "/admin/displays", icon: Monitor, label: "Displays", roles: ["admin"] },
  { to: "/admin/wallpapers", icon: ImageIcon, label: "Wallpaper-Sets", roles: ["admin"] },
  { to: "/admin/werbung", icon: Megaphone, label: "Werbespots", roles: ["admin"] },
  { to: "/admin/newsletter", icon: Mail, label: "Newsletter", roles: ["admin"] },
  { to: "/admin/licht", icon: Lightbulb, label: "Licht & Rollos", roles: ["admin","staff"] },
  { to: "/admin/tv", icon: Tv, label: "TV-Steuerung", roles: ["admin"] },
  { to: "/admin/kampagnen", icon: TrendingUp, label: "Kampagnen", roles: ["admin"] },
  { to: "/admin/plakat", icon: Printer, label: "Wochen-Plakat", roles: ["admin"] },
  { to: "/admin/ticket-design", icon: PenTool, label: "Ticket-Design", roles: ["admin"] },
  { to: "/admin/documents", icon: FileText, label: "Dokumenten-Designer", roles: ["admin"] },
  { to: "/admin/contests", icon: Trophy, label: "Gewinnspiele", roles: ["admin"] },
  { to: "/admin/filmreihen", icon: Layers, label: "Filmreihen", roles: ["admin"] },
  { to: "/admin/marathon", icon: Ticket, label: "Marathon", roles: ["admin"] },
  { to: "/admin/trivia", icon: Sparkles, label: "Film-Trivia", roles: ["admin"] },
  { to: "/admin/reviews", icon: MessageSquare, label: "Bewertungen", roles: ["admin","staff"] },
  { to: "/admin/notfall-rebook", icon: AlertTriangle, label: "Notfall-Rückerfassung", roles: ["admin","staff"] },
  { to: "/admin/analytics", icon: BarChart3, label: "Analytics", roles: ["admin"] },
  { to: "/admin/statistics", icon: BarChart3, label: "Statistiken (klassisch)", roles: ["admin"] },
  { to: "/admin/scanner", icon: ScanLine, label: "Einlass", roles: ["admin","staff"] },
  { to: "/admin/saal-kontrolle", icon: ShieldCheck, label: "Saal-Kontrolle", roles: ["admin","staff"] },
  { to: "/admin/settings", icon: Settings, label: "Einstellungen", roles: ["admin"] },
  { to: "/admin/users", icon: UserCog, label: "Benutzer", roles: ["admin"] },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const items = NAV.filter((n) => n.roles.includes(user?.role));

  return (
    <div className="min-h-screen flex bg-[#050505] text-[#F5F0E6]">
      <aside data-testid="admin-sidebar" className="w-64 shrink-0 hidden lg:flex flex-col border-r border-[#1A1A1A] bg-[#080808]">
        <div className="px-6 pt-8 pb-6 border-b border-[#1A1A1A]">
          <CinemaBrand size="sm" stacked />
        </div>
        <nav className="flex-1 py-4 px-3 overflow-y-auto no-scrollbar">
          {items.map(({ to, icon: Icon, label, exact }) => (
            <NavLink
              key={to}
              to={to}
              end={exact}
              data-testid={`nav-${label.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/ä/g,"a").replace(/ö/g,"o").replace(/ü/g,"u").replace(/ß/g,"ss").replace(/[^a-z0-9]+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm mb-0.5 transitions-safe ${
                  isActive
                    ? "bg-[#1A1A1A] text-[#D4AF37] border-l-2 border-[#D4AF37] pl-[10px]"
                    : "text-[#A8A398] hover:text-[#F5F0E6] hover:bg-[#121212]"
                }`
              }
            >
              <Icon size={17} strokeWidth={1.4} />
              <span className="font-light">{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[#1A1A1A]">
          <div className="text-xs text-[#A8A398] mb-2 truncate" data-testid="admin-user-email">{user?.email}</div>
          <button
            data-testid="admin-logout-btn"
            onClick={async () => { await logout(); navigate("/login"); }}
            className="w-full flex items-center justify-center gap-2 text-xs uppercase tracking-[0.2em] py-2 rounded-md border border-[#2A2A2A] text-[#A8A398] hover:text-[#F5F0E6] hover:border-[#D4AF37] transitions-safe"
          >
            <LogOut size={14} /> Abmelden
          </button>
        </div>
      </aside>
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 backdrop-blur-xl bg-black/70 border-b border-[#1A1A1A] px-4 py-3 flex items-center justify-between">
        <div className="font-heading text-[#D4AF37] text-base sm:text-lg truncate">THE MOVIE HOUSE</div>
        <button
          data-testid="mobile-logout-btn"
          onClick={async () => { await logout(); navigate("/login"); }}
          className="text-[10px] text-[#A8A398] uppercase tracking-widest px-3 py-1.5 border border-[#2A2A2A] rounded-md active:bg-[#1A1A1A] shrink-0"
        >
          Abmelden
        </button>
      </div>
      <main className="flex-1 min-w-0 pt-14 lg:pt-0">
        <div className="lg:hidden overflow-x-auto no-scrollbar border-b border-[#1A1A1A] px-3 py-2.5 flex gap-2 sticky top-14 bg-[#050505]/95 backdrop-blur z-30">
          {items.map(({ to, icon: Icon, label, exact }) => (
            <NavLink key={to} to={to} end={exact}
              className={({ isActive }) => `whitespace-nowrap text-xs px-3.5 py-2 rounded-md inline-flex items-center gap-1.5 shrink-0 ${isActive ? "bg-[#D4AF37] text-black font-medium" : "bg-[#121212] text-[#A8A398] active:bg-[#1A1A1A]"}`}
            >
              <Icon size={13} strokeWidth={1.6} /> {label}
            </NavLink>
          ))}
        </div>
        <div className="p-3 sm:p-4 md:p-8 lg:p-10 max-w-[1600px] mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
